import React from 'react';
import {useSuspenseQuery, useQueryClient, QueryClient} from '@tanstack/react-query';
import useClientServerEffect from './useClientServerEffect';
import useShellGate from './useShellGate';

/**
 * Context that supplies fallback provider components for deferred data.
 * Consumed inside `withSSRPage` — fallbacks only resolve when no page-level
 * provider has registered intent via `useRegisterDeferredProvider`.
 */
export const DeferredFallbacksContext = React.createContext<React.ComponentType[]>([]);

const DEFERREDS = Symbol('deferreds');
const PROVIDER_INTENTS = Symbol('provider-intents');

/**
 * A manually-controlled Promise: the creator holds `resolve` and can settle
 * the `promise` at any time from a different call site.
 * @template T - The type of the deferred value.
 */
interface Deferred<T = unknown> {
	promise: Promise<T>;
	resolve: (value: T) => void;
}

/**
 * Returns the per-request map of deferred promises stored on the QueryClient.
 * Creates the map lazily on first access.
 * @param {QueryClient} qc - The QueryClient instance scoped to the current request.
 * @returns {Map<string, Deferred>} Map keyed by serialized query key.
 */
function getDeferredMap(qc: QueryClient): Map<string, Deferred> {
	const store = qc as any;

	if (!store[DEFERREDS]) {
		store[DEFERREDS] = new Map();
	}

	return store[DEFERREDS];
}

/**
 * Returns the per-request set of provider intents stored on the QueryClient.
 * An intent signals that a page-level component will provide a value for a
 * given key, so fallback providers should not resolve it prematurely.
 * @param {QueryClient} qc - The QueryClient instance scoped to the current request.
 * @returns {Set<string>} Set of serialized query keys with registered intents.
 */
function getProviderIntentSet(qc: QueryClient): Set<string> {
	const store = qc as any;

	if (!store[PROVIDER_INTENTS]) {
		store[PROVIDER_INTENTS] = new Set();
	}

	return store[PROVIDER_INTENTS];
}

/**
 * Retrieves an existing deferred for the given key, or creates a new one.
 * The deferred's promise is used as the `queryFn` for `useSuspenseQuery`,
 * so resolving it unblocks the suspended consumer.
 * @template T
 * @param {QueryClient} qc - The QueryClient instance scoped to the current request.
 * @param {string} serializedKey - JSON-serialized query key.
 * @returns {Deferred<T>} The existing or newly created deferred.
 */
function getOrCreateDeferred<T>(qc: QueryClient, serializedKey: string): Deferred<T> {
	const map = getDeferredMap(qc);

	if (!map.has(serializedKey)) {
		let resolve!: (v: T) => void;

		const promise = new Promise<T>((r) => {
			resolve = r;
		});

		map.set(serializedKey, {promise, resolve} as Deferred);
	}

	return map.get(serializedKey)! as Deferred<T>;
}

/**
 * Reads a value that another component will provide via `useProvideDeferredData`.
 * Suspends until the value is available. Works on SSR: outside a `NonCriticalBoundary`
 * the shell waits for it (see `coreUtils/ssrShell`).
 *
 * The provider and consumer are decoupled via `QueryClient`: they can live
 * anywhere in the tree (ancestor reads, descendant provides, or vice versa).
 *
 * Complementary to `useServerData`:
 * - `useServerData`: data comes from an async fetch (server → component)
 * - `useDeferredData`: data comes from a component that renders later in the tree
 *
 * The resolved value is automatically dehydrated by `dehydrate(queryClient)` and
 * rehydrated on the client via `HydrationBoundary`, so no re-fetch or suspend
 * occurs during hydration.
 *
 * @param key  - Unique cache key (same convention as useServerData)
 * @param deps - Additional dependencies appended to the query key
 */
export function useDeferredData<T>(key: string, deps: unknown[] = []): T {
	const qc = useQueryClient();
	const queryKey = [key, ...deps].filter((d) => d != null);
	const serializedKey = JSON.stringify(queryKey);
	// Server: the shell waits for the value (SEO, shell header), see `coreUtils/ssrShell`.
	const gate = useShellGate();
	const gateId = gate && `deferred:${serializedKey}`;

	gate?.begin(gateId);

	const {data} = useSuspenseQuery<T>({
		queryKey,
		queryFn: () => getOrCreateDeferred<T>(qc, serializedKey).promise,
		staleTime: Infinity,
		gcTime: 0,
	});

	gate?.end(gateId);

	return data;
}

/**
 * Registers intent to provide a deferred value. Call this BEFORE any
 * suspending hook (`useServerData`) so the intent is recorded even if
 * the component suspends. Fallback providers check this intent and
 * skip resolution when a real provider is registered.
 *
 * @param key  - Must match the provider's key
 * @param deps - Must match the provider's deps
 */
export function useRegisterDeferredProvider(key: string, deps: unknown[] = []): void {
	const qc = useQueryClient();
	const serializedKey = JSON.stringify([key, ...deps].filter((d) => d != null));

	getProviderIntentSet(qc).add(serializedKey);
}

/**
 * Provides a value for `useDeferredData` consumers. Resolves any suspended
 * consumer on the first call. The value is dehydrated for client hydration.
 *
 * On SSR, `useClientServerEffect` runs the effect synchronously, so the
 * deferred promise resolves during the same render pass and React retries
 * the suspended boundary before `onAllReady` fires.
 *
 * When `isFallback` is true, the resolve is skipped if a page-level provider
 * has registered intent via `useRegisterDeferredProvider`. This prevents
 * fallbacks from resolving prematurely when React renders siblings of a
 * suspended page component.
 *
 * @param key   - Must match the consumer's key
 * @param value - The value to provide
 * @param deps  - Must match the consumer's deps
 * @param options
 * @param options.isFallback - If true, skip when a real provider is registered
 */
/**
 * Plain-function variant of `useProvideDeferredData`. Safe to call outside
 * React components (e.g. inside a queryFn or utility function).
 * Resolves the deferred promise and sets the query cache entry so that any
 * `useDeferredData` consumer with the same key stops suspending.
 *
 * On the client this is typically unnecessary (React Query rehydrates the
 * cache from the server), but it can be called as a no-op without harm.
 */
export function resolveDeferred<T>(
	qc: QueryClient,
	key: string,
	value: T,
	deps: unknown[] = [],
): void {
	const queryKey = [key, ...deps].filter((d) => d != null);
	const serializedKey = JSON.stringify(queryKey);

	getOrCreateDeferred<T>(qc, serializedKey).resolve(value);
	qc.setQueryData(queryKey, value);
}

/**
 * Provides a value for {@link useDeferredData} consumers within a React component.
 *
 * **Two-phase resolution:**
 * 1. **Synchronous (inline)** — resolves the deferred promise and seeds the query cache
 *    immediately during the render pass. On SSR this unblocks suspended consumers
 *    before `onAllReady` fires. Skipped when `isFallback` is `true` and a page-level
 *    provider has already registered intent via {@link useRegisterDeferredProvider}.
 * 2. **Effect (`useClientServerEffect`)** — re-resolves on value changes and cleans up
 *    the deferred map, intent set, and query cache on unmount so the next navigation
 *    starts fresh.
 *
 * @typeParam T - Shape of the provided value.
 * @param key     - Cache key prefix; must match the consumer's key.
 * @param value   - The value to provide.
 * @param deps    - Additional cache dependencies appended to `[key]`. Defaults to `[]`.
 * @param options.isFallback - When `true`, the resolution is skipped if a real
 *   page-level provider has registered intent for the same key+deps, **or** if
 *   the query cache already contains data (e.g. hydrated from SSR).
 *   Use this in App-shell fallback components (e.g. `DefaultHeaderStyleFallback`).
 */
export function useProvideDeferredData<T>(
	key: string,
	value: T,
	deps: unknown[] = [],
	options?: {isFallback?: boolean},
): void {
	const qc = useQueryClient();
	const queryKey = [key, ...deps].filter((d) => d != null);
	const serializedKey = JSON.stringify(queryKey);

	const shouldSkipFallback = options?.isFallback && (
		getProviderIntentSet(qc).has(serializedKey) ||
		qc.getQueryData(queryKey) != null
	);

	if (!shouldSkipFallback) {
		getOrCreateDeferred<T>(qc, serializedKey).resolve(value);
		qc.setQueryData(queryKey, value);
	}

	useClientServerEffect(() => {
		if (options?.isFallback && (
			getProviderIntentSet(qc).has(serializedKey) ||
			qc.getQueryData(queryKey) != null
		)) {
			return () => {};
		}

		getOrCreateDeferred<T>(qc, serializedKey).resolve(value);

		return () => {
			getDeferredMap(qc).delete(serializedKey);
			getProviderIntentSet(qc).delete(serializedKey);
			qc.removeQueries({queryKey, exact: true});
		};
	}, [serializedKey]);
}
